/**
 * O menu do painel: a árvore que vem do servidor (`navigation_escola` na Escola,
 * `window.empresa_navigation` no Logigate, ambas `{nome, icon, url, badge, submenu}`), o
 * item aceso pelo endereço, a pesquisa sem acentos e a lista achatada da paleta ⌘K.
 */
import type { ReactNode } from "react";

export interface ItemPainel {
    /** Única na árvore (o caminho dos nomes). */
    chave: string;
    rotulo: string;
    /** Um ícone do Iconify (`mdi:school`). */
    icone?: string;
    /**
     * A cor do ícone, num nome do Tailwind (`sky`, `teal`). Sem ela, o ícone fica na cor do menu
     * (os ícones do menu são monocromáticos: hoje nenhum item a traz).
     */
    cor?: string;
    /** Para que serve a entrada, numa linha: a dica ao passar o rato e a pesquisa da paleta. */
    descricao?: string;
    href?: string;
    /** Abre noutro separador (`target="_blank"`). */
    externo?: boolean;
    contagem?: number | string | null;
    itens?: ItemPainel[];
    /** Algo a mostrar no topo do grupo, antes dos sub-itens (um seletor de escola, de hotel…). */
    antesDosItens?: ReactNode;
}

/** A forma do menu que vem do servidor. */
export interface ItemMenuServidor {
    nome: string;
    url?: string | null;
    icon?: string | null;
    /** A cor do ícone (nome do Tailwind), como `ItemPainel.cor`. */
    cor?: string | null;
    /** Para que serve, numa linha (como `ItemPainel.descricao`). */
    descricao?: string | null;
    badge?: string | number | null;
    target?: string | null;
    submenu?: ItemMenuServidor[] | null;
    [extra: string]: unknown;
}

/**
 * Converte o menu do servidor. `contagens` sobrepõe-se aos `badge` e vai pela `chave` (o caminho
 * dos nomes, `Gestão Condominial›Reservas`), não pelo nome: há nomes repetidos em módulos
 * diferentes («Notificações» do painel e a configuração das do Condomínio, «Reservas» do
 * Condomínio e da Hotelaria), e o número de um não pode aparecer no outro.
 */
export function menuDoServidor(itens: ItemMenuServidor[] | null | undefined, contagens?: Record<string, string | number>, pai = ""): ItemPainel[] {
    if (!Array.isArray(itens)) {
        return [];
    }
    return itens
        .filter((i) => i && typeof i === "object" && i.nome)
        .map((i) => {
            const chave = pai ? `${pai}›${i.nome}` : i.nome;
            const filhos = Array.isArray(i.submenu) && i.submenu.length > 0 ? menuDoServidor(i.submenu, contagens, chave) : undefined;
            const contagem = contagens?.[chave] ?? i.badge ?? null;
            return {
                chave,
                rotulo: i.nome,
                icone: i.icon ?? undefined,
                cor: typeof i.cor === "string" && i.cor ? i.cor : undefined,
                descricao: typeof i.descricao === "string" && i.descricao ? i.descricao : undefined,
                href: filhos ? undefined : (i.url ?? undefined),
                externo: i.target === "_blank",
                contagem: contagem === "" ? null : contagem,
                itens: filhos,
            };
        });
}

/** O caminho de um `href` (relativo ou absoluto) e a query; `null` se for doutro sítio. */
export function partesDoEndereco(href: string): { caminho: string; query: URLSearchParams } | null {
    try {
        const base = typeof window === "undefined" ? "http://painel.local" : window.location.origin;
        const u = new URL(href, base);
        if (typeof window !== "undefined" && u.origin !== window.location.origin) {
            return null;
        }
        return { caminho: u.pathname.replace(/\/+$/, "") || "/", query: u.searchParams };
    } catch {
        return null;
    }
}

function folhas(itens: ItemPainel[], resultado: ItemPainel[] = []): ItemPainel[] {
    for (const i of itens) {
        if (i.itens?.length) {
            folhas(i.itens, resultado);
        } else if (i.href) {
            resultado.push(i);
        }
    }
    return resultado;
}

/**
 * O caminho do item aceso (do grupo de cima até à folha), para acender o menu, abrir os
 * grupos e fazer o trilho. Primeiro o endereço exacto (com a query do item, se a tiver);
 * depois a folha mais comprida que é prefixo do endereço (`/alunos/15` acende «Alunos»),
 * menos a raiz do painel, que é prefixo de tudo.
 */
export function caminhoActivo(itens: ItemPainel[], url: string): ItemPainel[] {
    const actual = partesDoEndereco(url);
    if (!actual) {
        return [];
    }
    const todas = folhas(itens);
    const comPartes = todas
        .map((f) => ({ f, p: f.href && !f.externo ? partesDoEndereco(f.href) : null }))
        .filter((x): x is { f: ItemPainel; p: { caminho: string; query: URLSearchParams } } => x.p !== null);

    const bateQuery = (q: URLSearchParams) => {
        for (const [k, v] of q.entries()) {
            if (actual.query.get(k) !== v) {
                return false;
            }
        }
        return true;
    };

    // O mesmo caminho: primeiro o que tem a query toda igual (os separadores `?tab=`), depois
    // qualquer um (os links da Escola dentro do Logigate trazem `?escola_id=` e o endereço não).
    const mesmoCaminho = comPartes.filter((x) => x.p.caminho === actual.caminho);
    let escolhida =
        mesmoCaminho.filter((x) => bateQuery(x.p.query)).sort((a, b) => [...b.p.query.keys()].length - [...a.p.query.keys()].length)[0]?.f ??
        mesmoCaminho[0]?.f;

    if (!escolhida) {
        const raiz = comPartes.reduce<string | null>((menor, x) => (menor === null || x.p.caminho.length < menor.length ? x.p.caminho : menor), null);
        escolhida = comPartes
            .filter((x) => x.p.caminho !== raiz && x.p.caminho !== "/" && actual.caminho.startsWith(`${x.p.caminho}/`))
            .sort((a, b) => b.p.caminho.length - a.p.caminho.length)[0]?.f;
    }

    if (!escolhida) {
        return [];
    }

    const procurar = (lista: ItemPainel[], acumulado: ItemPainel[]): ItemPainel[] | null => {
        for (const i of lista) {
            if (i === escolhida) {
                return [...acumulado, i];
            }
            if (i.itens) {
                const r = procurar(i.itens, [...acumulado, i]);
                if (r) {
                    return r;
                }
            }
        }
        return null;
    };
    return procurar(itens, []) ?? [];
}

/** Minúsculas e sem acentos («Inscrição» → «inscricao»). */
export function semAcentos(texto: string): string {
    return texto.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
}

/**
 * A árvore só com o que bate na pesquisa: um grupo cujo nome bate fica com tudo; senão
 * fica com os filhos que batem. Todas as palavras têm de aparecer.
 */
export function filtrarMenu(itens: ItemPainel[], termo: string): ItemPainel[] {
    const palavras = semAcentos(termo).split(/\s+/).filter(Boolean);
    if (palavras.length === 0) {
        return itens;
    }
    const bate = (texto: string) => {
        const t = semAcentos(texto);
        return palavras.every((p) => t.includes(p));
    };
    const filtrar = (lista: ItemPainel[]): ItemPainel[] =>
        lista.reduce<ItemPainel[]>((acc, i) => {
            if (bate(i.rotulo)) {
                acc.push(i);
            } else if (i.itens) {
                const filhos = filtrar(i.itens);
                if (filhos.length) {
                    acc.push({ ...i, itens: filhos });
                }
            }
            return acc;
        }, []);
    return filtrar(itens);
}

export interface EntradaPaleta {
    chave: string;
    rotulo: string;
    /** «Alunos › Matrículas» — onde está no menu. */
    caminho: string;
    /** Para que serve (a `descricao` do item): entra na pesquisa. */
    descricao?: string;
    icone?: string;
    href: string;
    externo?: boolean;
}

/** Todas as folhas com o caminho no menu, para a paleta ⌘K (as de cima primeiro). */
export function achatarMenu(itens: ItemPainel[]): EntradaPaleta[] {
    const resultado: (EntradaPaleta & { fundura: number })[] = [];
    const andar = (lista: ItemPainel[], pais: string[], iconeRaiz?: string) => {
        for (const i of lista) {
            const icone = pais.length === 0 ? i.icone : iconeRaiz;
            if (i.itens?.length) {
                andar(i.itens, [...pais, i.rotulo], icone);
            } else if (i.href) {
                resultado.push({ chave: i.chave, rotulo: i.rotulo, caminho: pais.join(" › "), descricao: i.descricao, icone: i.icone ?? icone, href: i.href, externo: i.externo, fundura: pais.length });
            }
        }
    };
    andar(itens, []);
    return resultado.sort((a, b) => a.fundura - b.fundura).map(({ fundura: _f, ...e }) => e);
}

/** A soma das contagens numéricas de um grupo (o número no ícone do menu recolhido). */
export function somaDasContagens(item: ItemPainel): number {
    const propria = Number(item.contagem);
    if (Number.isFinite(propria) && propria > 0) {
        return propria;
    }
    if (!item.itens?.length) {
        return 0;
    }
    return item.itens.reduce((s, i) => s + somaDasContagens(i), 0);
}
